import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Bot, PhoneForwarded } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import {
  STATE_LABELS,
  STATE_VARIANTS,
  SOURCE_LABELS,
  TRIGGER_LABELS,
  handoverStatusNote,
} from '../../lib/leadBot/queueView';
import { modeNotice } from '../../lib/leadBot/mode';
import { Transcript, isNearBottom } from '../../components/leadBot/Transcript';
import {
  approveDraft,
  closeConversation,
  getConversation,
  handBackConversation,
  handOffConversation,
  regenerateDraft,
  rejectDraft,
  setConversationOutcome,
} from './api';
import { resolveVertical } from '../../lib/leadBot/verticals';
import { DraftApprovalPanel } from './DraftApprovalPanel';
import { OutcomePicker } from './OutcomePicker';
import { AppointmentLine } from './AppointmentLine';
import { FitnessSummary } from './FitnessSummary';
import { DoNotContactControl } from './DoNotContactControl';
import { ConversationLeadHistory } from '../speedToLead';

// The conversation viewer + approval surface.
//
// ⚠️ BUILT TO BE KEPT. This is also the transcript viewer Phase 3 reporting
// needs, so it shows the WHOLE conversation — including drafts that were
// rejected or superseded and never reached the lead — rather than only what
// went out. "What did the bot want to say, and what did we actually send"
// is the question this phase exists to answer.

export { isNearBottom };

export function ConversationDetail({
  conversationId,
  smsConfigured,
}: {
  conversationId: string;
  smsConfigured: boolean;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['lead-bot', 'conversation', conversationId],
    queryFn: () => getConversation(conversationId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['lead-bot'] });
  };

  const approve = useMutation({
    mutationFn: (text: string) => approveDraft(data!.pendingDraft!.id, text),
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    onSuccess: (outcome) => {
      if (outcome.ok) toast.success('Sent.');
      // ⚠️ A refusal is rendered as an EXPLANATION, not an error toast that
      // vanishes: an unprovisioned number or an opt-out is something the AM has
      // to act on, and the draft is still sitting there.
      else toast.warning(outcome.detail, { duration: 10_000 });
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reject = useMutation({
    mutationFn: () => rejectDraft(data!.pendingDraft!.id),
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    onSuccess: () => {
      toast.success('Draft rejected.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const redraft = useMutation({
    mutationFn: () => regenerateDraft(conversationId),
    onMutate: () => setBusy(true),
    onSettled: () => setBusy(false),
    onSuccess: () => invalidate(),
    onError: (err: Error) => toast.error(err.message),
  });

  const handOff = useMutation({
    mutationFn: () => handOffConversation(conversationId, null),
    onSuccess: () => {
      toast.success('Flagged for a person.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handBack = useMutation({
    mutationFn: () => handBackConversation(conversationId),
    onSuccess: () => {
      toast.success('Handed back to the assistant.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const close = useMutation({
    mutationFn: () => closeConversation(conversationId),
    onSuccess: () => {
      toast.success('Conversation closed.');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading || !data) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const { conversation, messages, pendingDraft, config } = data;
  const notice = config ? modeNotice(config.mode, config.testPhoneAllowlist?.length ?? 0) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* header */}
      <div className="max-h-[45%] shrink-0 overflow-y-auto border-b bg-card px-4 py-3 scroll-subtle">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">{conversation.leadName}</h2>
              <Badge variant={STATE_VARIANTS[conversation.state]}>
                {STATE_LABELS[conversation.state]}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {conversation.leadPhone}
              {conversation.accountName ? ` · ${conversation.accountName}` : ''}
              {conversation.campaign ? ` · ${conversation.campaign}` : ''}
            </p>
            <div className="mt-2"><ConversationLeadHistory conversationId={conversationId} confirmOutcome={async (id, code) => { await setConversationOutcome(id, code); invalidate(); }} /></div>
          </div>
          <div className="flex items-center gap-2">
            <DoNotContactControl conversationId={conversationId} clubName={conversation.accountName ?? null} />
            {conversation.state === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handOff.mutate()}
                disabled={handOff.isPending}
              >
                <PhoneForwarded className="h-4 w-4" />
                Hand to a person
              </Button>
            )}
            {/* After a handover the assistant is quiet. This hands it back (audited). Not offered once
                distress stopped the conversation: a person reaches out personally. */}
            {conversation.state === 'handoff' && !conversation.distressAt && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handBack.mutate()}
                disabled={handBack.isPending}
              >
                <Bot className="h-4 w-4" />
                Hand back to {config?.assistantName ?? 'the assistant'}
              </Button>
            )}
            {conversation.state !== 'closed' && conversation.state !== 'stopped' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => close.mutate()}
                disabled={close.isPending}
              >
                Close
              </Button>
            )}
          </div>
        </div>

        {conversation.state === 'handoff' && conversation.handoffTriggers.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {conversation.handoffTriggers.map((trigger) => (
              <Badge
                key={trigger.kind}
                variant={trigger.kind === 'compliance' ? 'danger' : 'warning'}
                title={`${trigger.detail} — ${SOURCE_LABELS[trigger.source]}`}
              >
                {TRIGGER_LABELS[trigger.kind]}
              </Badge>
            ))}
            {/* ⚠️ The two modes read differently and the AM needs to know which
                one the lead was promised. */}
            <span className="text-xs text-muted-foreground">
              {conversation.handoffMode === 'notify'
                ? 'flagged during business hours — the team should pick this up now'
                : 'flagged outside business hours — the lead was offered a callback'}
              {' · '}
              {handoverStatusNote(conversation, {
                fitness: config ? resolveVertical(config) === 'fitness' : false,
                assistantName: config?.assistantName ?? '',
              })}
            </span>
          </div>
        )}

        <FitnessSummary conversation={conversation} />

        {data.appointment && <AppointmentLine appointment={data.appointment} />}

        {/* ⚠️ ALWAYS SHOWN, in every state. An outcome usually becomes known
            after the conversation ends, and it cannot be reconstructed later —
            see OutcomePicker. */}
        <div className="mt-2">
          <OutcomePicker conversation={conversation} onSaved={invalidate} />
        </div>

        {conversation.state === 'stopped' && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-rose-700">
            <Ban className="h-3.5 w-3.5" />
            This lead sent STOP. Nothing further can be sent to this number.
          </p>
        )}

        {notice && <p className="mt-2 text-xs text-amber-700">{notice}</p>}
      </div>

      {/* the transcript — the ONLY scrolling region, so the composer stays put */}
      <Transcript conversationId={conversationId} messages={messages} internals />

      {pendingDraft ? (
        <DraftApprovalPanel
          draft={pendingDraft}
          smsConfigured={smsConfigured}
          busy={busy}
          // ⚠️ AN AUTONOMOUS BOT HAS NO APPROVAL STEP. A draft still sitting
          // here means the automatic send did NOT happen, so the panel becomes
          // an exception handler ("this did not go out — send it?") rather than
          // a review queue. The control stays because a failed send would
          // otherwise be stranded with no way to push it; what is removed is
          // the pretence that a person is meant to be reviewing every reply.
          autonomous={config?.mode === 'autonomous'}
          onApprove={(text) => approve.mutate(text)}
          onReject={() => reject.mutate()}
          onRegenerate={() => redraft.mutate()}
        />
      ) : (
        <div className="shrink-0 border-t bg-card p-4 text-sm text-muted-foreground">
          {conversation.state === 'stopped' ? (
            'No reply can be drafted — this lead has opted out.'
          ) : conversation.state === 'handoff' ? (
            'Handed to the team: the assistant will not reply. Hand it back above to let it answer again.'
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span>No reply is waiting for approval.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => redraft.mutate()}
                disabled={redraft.isPending}
              >
                {redraft.isPending ? <Spinner className="h-4 w-4" /> : null}
                Draft a reply
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
